import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Applies an "equals" filter (read from a URL query param) on a MaterialUiTable
// column, so another screen can link to the table pre-filtered.
// Uses the grid apiRef (upsertFilterItem / deleteFilterItem).
export const useUrlTableFilter = (
  apiRef: ReturnType<typeof useGridApiRef>,
  isReady: boolean,
  config: {
    paramName: string;
    field: string;
    filterId: string;
    // Maps the raw URL param to the value stored in the column. Returning
    // undefined removes the filter (e.g. the param has no match in the list).
    // When omitted, the raw param is used as-is.
    resolveValue?: (rawParam: string) => string | undefined;
  },
) => {
  const [searchParams] = useSearchParams();
  const rawParam = searchParams.get(config.paramName);
  const { field, filterId, resolveValue } = config;

  useEffect(() => {
    const listIsNotReady =
      !apiRef.current?.upsertFilterItem ||
      !apiRef.current?.deleteFilterItem ||
      !isReady;

    if (listIsNotReady) {
      return;
    }

    const alreadyInFilter = apiRef.current.state.filter.filterModel.items.find(
      (item) => item.id === filterId,
    );

    const value =
      rawParam === null
        ? undefined
        : resolveValue
          ? resolveValue(rawParam)
          : rawParam;

    if (value === undefined) {
      if (alreadyInFilter) {
        apiRef.current.deleteFilterItem(alreadyInFilter);
      }
      return;
    }

    apiRef.current.upsertFilterItem({
      id: filterId,
      field,
      operator: "equals",
      value,
    });
  }, [apiRef, isReady, rawParam, field, filterId, resolveValue]);
};
